import { useEffect, useRef, useState } from 'react';
import { ClassScreen, type ClassView } from '../classrooms/ClassScreen';

/**
 * Fuentes de video de la computadora de proyección: cámaras web y capturadoras USB. Viven en esta
 * computadora (como los archivos locales): nombre y dispositivo. El programa y el estado en vivo
 * solo llevan su id; las salidas, que están en la misma computadora, abren la cámara ellas mismas.
 */
export interface VideoSource {
  id: string;
  name: string;
  deviceId: string;
  /** Nombre del dispositivo: respaldo si Windows le cambia el deviceId. */
  label: string;
  audioDeviceId?: string | null;
  audioLabel?: string | null;
  /** Fuente «Clase en vivo» de Raíces: no es cámara; la pantalla muestra esa vista de la clase. */
  classLive?: { churchId: string; classroomId: string; view: ClassView } | null;
}

const KEY = 'keora.consola.fuentes';

export function loadSources(): VideoSource[] {
  try {
    return (JSON.parse(localStorage.getItem(KEY) ?? '[]') as VideoSource[]).filter((s) => s && /^[A-Za-z0-9_-]{1,64}$/.test(s.id));
  } catch {
    return [];
  }
}

export function saveSources(list: VideoSource[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Sin almacenamiento: duran mientras la consola esté abierta.
  }
}

export const newSourceId = () => `src-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export const mediaDevicesOk = () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

/** Cámaras y micrófonos de esta computadora (pide permiso una vez para poder ver sus nombres). */
export async function listDevices(): Promise<{ video: MediaDeviceInfo[]; audio: MediaDeviceInfo[] }> {
  if (!mediaDevicesOk()) throw new Error('Este navegador no deja usar cámaras. Usa la app KEORA Multimedia, Chrome o Edge.');
  let devices = await navigator.mediaDevices.enumerateDevices();
  if (!devices.some((d) => d.kind === 'videoinput' && d.label)) {
    const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    s.getTracks().forEach((t) => t.stop());
    devices = await navigator.mediaDevices.enumerateDevices();
  }
  return { video: devices.filter((d) => d.kind === 'videoinput'), audio: devices.filter((d) => d.kind === 'audioinput') };
}

/** El deviceId de hoy: el guardado, o el del dispositivo con el mismo nombre si cambió. */
async function currentIds(src: VideoSource): Promise<{ video: string; audio: string | null }> {
  const devices = await navigator.mediaDevices.enumerateDevices();
  const video = devices.find((d) => d.kind === 'videoinput' && d.deviceId === src.deviceId) ?? devices.find((d) => d.kind === 'videoinput' && d.label && d.label === src.label);
  const audio = src.audioDeviceId ? (devices.find((d) => d.kind === 'audioinput' && d.deviceId === src.audioDeviceId) ?? devices.find((d) => d.kind === 'audioinput' && d.label && d.label === src.audioLabel)) : undefined;
  return { video: video?.deviceId || src.deviceId, audio: audio?.deviceId ?? null };
}

/**
 * Video en vivo de una fuente. `withAudio` solo en la pantalla principal (las miniaturas van sin
 * sonido). Si la fuente no está en esta computadora o no hay permiso, lo dice en lugar del video.
 */
export function CameraFeed({ sourceId, className, withAudio = false }: { sourceId: string; className?: string; withAudio?: boolean }) {
  const cls = loadSources().find((s) => s.id === sourceId)?.classLive;
  if (cls) return <ClassScreen churchId={cls.churchId} classroomId={cls.classroomId} view={cls.view} className={className} />;
  return <DeviceFeed sourceId={sourceId} className={className} withAudio={withAudio} />;
}

function DeviceFeed({ sourceId, className, withAudio = false }: { sourceId: string; className?: string; withAudio?: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const src = loadSources().find((s) => s.id === sourceId) ?? null;
  const name = src?.name ?? null;

  useEffect(() => {
    if (!src) {
      setProblem('Esta fuente de video es de otra computadora.');
      return;
    }
    if (!mediaDevicesOk()) {
      setProblem('Este navegador no deja usar la cámara. Abre las pantallas con la app KEORA Multimedia, Chrome o Edge.');
      return;
    }
    let alive = true;
    let stream: MediaStream | null = null;
    setProblem(null);
    void (async () => {
      try {
        const ids = await currentIds(src);
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: ids.video }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: withAudio && ids.audio ? { deviceId: { exact: ids.audio }, echoCancellation: false, noiseSuppression: false, autoGainControl: false } : false,
        });
        if (!alive) return stream.getTracks().forEach((t) => t.stop());
        if (ref.current) ref.current.srcObject = stream;
      } catch (e) {
        if (!alive) return;
        const n = (e as { name?: string }).name;
        setProblem(n === 'NotAllowedError' ? 'No hay permiso para usar la cámara. Dale permiso en el candado junto a la dirección (o usa la app KEORA Multimedia).' : `No se encontró «${src.name}» en esta computadora. ¿Está conectada?`);
      }
    })();
    return () => {
      alive = false;
      stream?.getTracks().forEach((t) => t.stop());
    };
    // La fuente se identifica por su id y su dispositivo.
  }, [sourceId, src?.deviceId, src?.audioDeviceId, withAudio]); // eslint-disable-line react-hooks/exhaustive-deps

  if (problem) return <p className="camera__problem">{problem}</p>;
  return <video ref={ref} className={className} autoPlay playsInline muted={!withAudio} aria-label={name ?? 'Cámara'} />;
}
